import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
    Search,
    ShoppingCart,
    Heart,
    User,
    LogIn,
    Menu,
    X,
    UserCircle,
    ShoppingBag,
    LogOut,
    Info,
    Home,
    Phone,
    Package,
    ChevronDown,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { isLogin } from "../../auth";
import SearchOverlay from "../SearchOverlay/SearchOverlay";
import { cn } from "../../lib/utils";

const Header = () => {
    const isAuthenticated = isLogin();
    const location = useLocation();
    const { items: cartItems } = useSelector((state) => state.cart);
    const { items: wishlistItems } = useSelector((state) => state.wishlist);
    const [searchOpen, setSearchOpen] = useState(false);
    const [profileMenuOpen, setProfileMenuOpen] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (profileMenuOpen && !event.target.closest(".profile-menu-container")) {
                setProfileMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [profileMenuOpen]);

    // Close mobile menu on route change
    useEffect(() => {
        setMobileMenuOpen(false);
    }, [location.pathname]);

    const handleLogout = () => {
        dispatch({ type: "LOGOUT" });
        localStorage.removeItem("auth0");
        navigate("/account-login");
        setProfileMenuOpen(false);
    };

    const navLinks = [
        { name: "Home", path: "/", icon: Home },
        { name: "About", path: "/about-us", icon: Info },
        { name: "Contact", path: "/contact", icon: Phone },
    ];

    const isActive = (path) => location.pathname === path;

    return (
        <>
            <header
                className={cn(
                    "fixed top-0 left-0 w-full z-50 transition-all duration-500",
                    scrolled
                        ? "bg-white/95 backdrop-blur-md shadow-lg py-2"
                        : "bg-white py-3"
                )}
            >
                {/* Top Bar - Only on desktop */}
                <div className={cn(
                    "hidden lg:block border-b border-gray-100 transition-all duration-300 overflow-hidden",
                    scrolled ? "h-0 opacity-0" : "h-auto opacity-100 pb-2"
                )}>
                    <div className="container mx-auto px-4">
                        <div className="flex justify-between items-center text-xs text-gray-500">
                            <div className="flex items-center gap-4">
                                <a href="tel:+919712027909" className="flex items-center gap-1 hover:text-primary transition-colors">
                                    <Phone className="w-3 h-3" /> +91 9712027909
                                </a>
                                <span className="text-gray-300">|</span>
                                <a href="mailto:cressa2024@gmail.com" className="hover:text-primary transition-colors">cressa2024@gmail.com</a>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main Header */}
                <div className="container mx-auto px-4">
                    <div className="flex items-center justify-between">
                        {/* Logo */}
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            className="flex-shrink-0"
                        >
                            <Link to="/" className="block">
                                <img
                                    src="/assets/images/logo.png"
                                    alt="Cressa"
                                    className={cn(
                                        "w-auto object-contain transition-all duration-300",
                                        scrolled ? "h-16" : "h-20"
                                    )}
                                />
                            </Link>
                        </motion.div>

                        {/* Desktop Navigation */}
                        <nav className="hidden lg:flex items-center space-x-1">
                            {navLinks.map((link, idx) => (
                                <motion.div
                                    key={link.name}
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: idx * 0.1 }}
                                >
                                    <Link
                                        to={link.path}
                                        className={cn(
                                            "relative px-4 py-2 font-medium transition-all duration-200 rounded-full",
                                            isActive(link.path)
                                                ? "text-primary bg-primary/10"
                                                : "text-gray-700 hover:text-primary hover:bg-gray-50"
                                        )}
                                    >
                                        {link.name}
                                        {isActive(link.path) && (
                                            <motion.div
                                                layoutId="activeNav"
                                                className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 bg-primary rounded-full"
                                            />
                                        )}
                                    </Link>
                                </motion.div>
                            ))}
                        </nav>

                        {/* Actions */}
                        <div className="flex items-center gap-1">
                            {isAuthenticated ? (
                                <>
                                    {/* Search */}
                                    <motion.button
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        onClick={() => setSearchOpen(!searchOpen)}
                                        className="p-2.5 text-gray-600 hover:text-primary hover:bg-primary/10 rounded-full transition-all"
                                        aria-label="Search"
                                    >
                                        <Search className="w-5 h-5" />
                                    </motion.button>

                                    {/* Cart */}
                                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                        <Link
                                            to="/product-cart"
                                            className="relative p-2.5 text-gray-600 hover:text-primary hover:bg-primary/10 rounded-full transition-all block"
                                        >
                                            <ShoppingCart className="w-5 h-5" />
                                            <AnimatePresence>
                                                {cartItems?.length > 0 && (
                                                    <motion.span
                                                        initial={{ scale: 0 }}
                                                        animate={{ scale: 1 }}
                                                        exit={{ scale: 0 }}
                                                        className="absolute -top-0.5 -right-0.5 w-5 h-5 text-[10px] font-bold text-white bg-primary rounded-full flex items-center justify-center shadow-lg"
                                                    >
                                                        {cartItems.length}
                                                    </motion.span>
                                                )}
                                            </AnimatePresence>
                                        </Link>
                                    </motion.div>

                                    {/* Wishlist */}
                                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                        <Link
                                            to="/product-wishlist"
                                            className="relative p-2.5 text-gray-600 hover:text-primary hover:bg-primary/10 rounded-full transition-all block"
                                        >
                                            <Heart className="w-5 h-5" />
                                            <AnimatePresence>
                                                {wishlistItems?.length > 0 && (
                                                    <motion.span
                                                        initial={{ scale: 0 }}
                                                        animate={{ scale: 1 }}
                                                        exit={{ scale: 0 }}
                                                        className="absolute -top-0.5 -right-0.5 w-5 h-5 text-[10px] font-bold text-white bg-red-500 rounded-full flex items-center justify-center shadow-lg"
                                                    >
                                                        {wishlistItems.length}
                                                    </motion.span>
                                                )}
                                            </AnimatePresence>
                                        </Link>
                                    </motion.div>

                                    {/* Profile Dropdown */}
                                    <div className="relative profile-menu-container">
                                        <motion.button
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                            onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                                            className={cn(
                                                "flex items-center gap-1 p-2.5 rounded-full transition-all",
                                                profileMenuOpen
                                                    ? "text-primary bg-primary/10"
                                                    : "text-gray-600 hover:text-primary hover:bg-primary/10"
                                            )}
                                        >
                                            <User className="w-5 h-5" />
                                            <ChevronDown className={cn("w-3 h-3 transition-transform", profileMenuOpen && "rotate-180")} />
                                        </motion.button>

                                        <AnimatePresence>
                                            {profileMenuOpen && (
                                                <motion.div
                                                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                                                    transition={{ type: "spring", stiffness: 300, damping: 25 }}
                                                    className="absolute right-0 mt-2 w-56 bg-white rounded-2xl shadow-2xl border border-gray-100 py-2 z-50 overflow-hidden"
                                                >
                                                    <div className="px-4 py-3 bg-gradient-to-r from-primary/10 to-primary/5 border-b border-gray-100">
                                                        <p className="text-xs text-gray-500">Welcome back!</p>
                                                        <p className="font-semibold text-gray-800 truncate">My Account</p>
                                                    </div>
                                                    <Link
                                                        to="/my-account"
                                                        onClick={() => setProfileMenuOpen(false)}
                                                        className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 hover:text-primary transition-colors"
                                                    >
                                                        <UserCircle className="w-4 h-4 mr-3 text-gray-400" /> Profile
                                                    </Link>
                                                    <Link
                                                        to="/my-orders"
                                                        onClick={() => setProfileMenuOpen(false)}
                                                        className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 hover:text-primary transition-colors"
                                                    >
                                                        <ShoppingBag className="w-4 h-4 mr-3 text-gray-400" /> My Orders
                                                    </Link>
                                                    <Link
                                                        to="/product-wishlist"
                                                        onClick={() => setProfileMenuOpen(false)}
                                                        className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 hover:text-primary transition-colors"
                                                    >
                                                        <Heart className="w-4 h-4 mr-3 text-gray-400" /> Wishlist
                                                    </Link>
                                                    <div className="border-t border-gray-100 mt-1 pt-1">
                                                        <button
                                                            onClick={handleLogout}
                                                            className="flex w-full items-center px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors"
                                                        >
                                                            <LogOut className="w-4 h-4 mr-3" /> Sign Out
                                                        </button>
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                </>
                            ) : (
                                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                                    <Link
                                        to="/account-login"
                                        className="hidden lg:flex items-center gap-2 bg-gradient-to-r from-primary to-primary-dark text-white px-6 py-2.5 rounded-full font-medium shadow-lg shadow-primary/30 hover:shadow-xl hover:shadow-primary/40 transition-all"
                                    >
                                        <LogIn className="w-4 h-4" /> Sign In
                                    </Link>
                                </motion.div>
                            )}

                            {/* Mobile Menu Toggle */}
                            <motion.button
                                whileTap={{ scale: 0.9 }}
                                className="lg:hidden p-2.5 text-gray-700 hover:bg-gray-100 rounded-full ml-1"
                                onClick={() => setMobileMenuOpen(true)}
                            >
                                <Menu className="w-6 h-6" />
                            </motion.button>
                        </div>
                    </div>
                </div>
            </header>

            <SearchOverlay isOpen={searchOpen} onClose={() => setSearchOpen(false)} />

            {/* Mobile Menu */}
            <AnimatePresence>
                {mobileMenuOpen && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setMobileMenuOpen(false)}
                            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 lg:hidden"
                        />
                        <motion.div
                            initial={{ x: "100%" }}
                            animate={{ x: 0 }}
                            exit={{ x: "100%" }}
                            transition={{ type: "spring", damping: 30, stiffness: 300 }}
                            className="fixed top-0 right-0 h-full w-[85%] max-w-sm bg-white z-[60] shadow-2xl overflow-y-auto lg:hidden"
                        >
                            {/* Mobile Header */}
                            <div className="sticky top-0 bg-white z-10 p-4 border-b border-gray-100 flex justify-between items-center">
                                <Link to="/" onClick={() => setMobileMenuOpen(false)}>
                                    <img src="/assets/images/logo.png" alt="Cressa" className="h-10 w-auto" />
                                </Link>
                                <motion.button
                                    whileTap={{ scale: 0.9 }}
                                    onClick={() => setMobileMenuOpen(false)}
                                    className="p-2 text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors"
                                >
                                    <X className="w-6 h-6" />
                                </motion.button>
                            </div>

                            {/* Mobile Nav Links */}
                            <div className="p-4 space-y-1">
                                {navLinks.map((link, idx) => (
                                    <motion.div
                                        key={link.name}
                                        initial={{ opacity: 0, x: 20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: idx * 0.05 }}
                                    >
                                        <Link
                                            to={link.path}
                                            onClick={() => setMobileMenuOpen(false)}
                                            className={cn(
                                                "flex items-center px-4 py-3.5 rounded-xl transition-all",
                                                isActive(link.path)
                                                    ? "bg-primary/10 text-primary"
                                                    : "text-gray-700 hover:bg-gray-50"
                                            )}
                                        >
                                            <link.icon className={cn("w-5 h-5 mr-3", isActive(link.path) ? "text-primary" : "text-gray-400")} />
                                            <span className="font-medium">{link.name}</span>
                                        </Link>
                                    </motion.div>
                                ))}

                                {isAuthenticated && (
                                    <>
                                        <div className="border-t border-gray-100 my-3" />
                                        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}>
                                            <Link
                                                to="/product-cart"
                                                onClick={() => setMobileMenuOpen(false)}
                                                className="flex items-center justify-between px-4 py-3.5 rounded-xl text-gray-700 hover:bg-gray-50 transition-all"
                                            >
                                                <div className="flex items-center">
                                                    <ShoppingCart className="w-5 h-5 mr-3 text-gray-400" />
                                                    <span className="font-medium">Cart</span>
                                                </div>
                                                {cartItems?.length > 0 && (
                                                    <span className="bg-primary text-white text-xs px-2.5 py-1 rounded-full font-bold">
                                                        {cartItems.length}
                                                    </span>
                                                )}
                                            </Link>
                                        </motion.div>
                                        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.25 }}>
                                            <Link
                                                to="/product-wishlist"
                                                onClick={() => setMobileMenuOpen(false)}
                                                className="flex items-center justify-between px-4 py-3.5 rounded-xl text-gray-700 hover:bg-gray-50 transition-all"
                                            >
                                                <div className="flex items-center">
                                                    <Heart className="w-5 h-5 mr-3 text-gray-400" />
                                                    <span className="font-medium">Wishlist</span>
                                                </div>
                                                {wishlistItems?.length > 0 && (
                                                    <span className="bg-red-500 text-white text-xs px-2.5 py-1 rounded-full font-bold">
                                                        {wishlistItems.length}
                                                    </span>
                                                )}
                                            </Link>
                                        </motion.div>
                                        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }}>
                                            <Link
                                                to="/my-account"
                                                onClick={() => setMobileMenuOpen(false)}
                                                className="flex items-center px-4 py-3.5 rounded-xl text-gray-700 hover:bg-gray-50 transition-all"
                                            >
                                                <UserCircle className="w-5 h-5 mr-3 text-gray-400" />
                                                <span className="font-medium">My Account</span>
                                            </Link>
                                        </motion.div>
                                        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.35 }}>
                                            <Link
                                                to="/my-orders"
                                                onClick={() => setMobileMenuOpen(false)}
                                                className="flex items-center px-4 py-3.5 rounded-xl text-gray-700 hover:bg-gray-50 transition-all"
                                            >
                                                <ShoppingBag className="w-5 h-5 mr-3 text-gray-400" />
                                                <span className="font-medium">My Orders</span>
                                            </Link>
                                        </motion.div>
                                        <div className="border-t border-gray-100 my-3" />
                                        <motion.button
                                            initial={{ opacity: 0, x: 20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: 0.4 }}
                                            onClick={() => {
                                                handleLogout();
                                                setMobileMenuOpen(false);
                                            }}
                                            className="w-full flex items-center px-4 py-3.5 rounded-xl text-red-600 hover:bg-red-50 transition-all"
                                        >
                                            <LogOut className="w-5 h-5 mr-3" />
                                            <span className="font-medium">Sign Out</span>
                                        </motion.button>
                                    </>
                                )}

                                {!isAuthenticated && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.2 }}
                                        className="pt-4"
                                    >
                                        <Link
                                            to="/account-login"
                                            onClick={() => setMobileMenuOpen(false)}
                                            className="flex w-full items-center justify-center gap-2 bg-gradient-to-r from-primary to-primary-dark text-white px-4 py-4 rounded-xl font-semibold shadow-lg shadow-primary/30"
                                        >
                                            <LogIn className="w-5 h-5" /> Sign In
                                        </Link>
                                    </motion.div>
                                )}
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </>
    );
};

export default Header;